import {Logger, StringUtils, Utils, ApiClientCommunication} from 'neo-common';
import {Communication} from 'neo-games';
import {ForceToolLayout, ForceToolUI, ForceToolUIState} from './ForceToolUI';
import {ForceToolApi} from './ForceToolApi';
import {ForceToolExternalSetup, ForceToolExternalSetupInfo, ForceToolSet} from './Types';

type ApiClientError = ApiClientCommunication.Types.ApiClientError;
type ResponseBase = ApiClientCommunication.ResponseBase;
type IRequest = Communication.Game.IRequest;
type SendRequestFunctionType = Communication.Game.SendRequestFunctionType;
type IGameClientInterceptor = Communication.Game.IGameClientInterceptor;
type InterceptorRequestContext = Communication.Game.InterceptorRequestContext;
var InitResponse = Communication.Game.InitResponse;
var GameResponse = Communication.Game.GameResponse;
var InitRequest = Communication.Game.InitRequest;
var SpinRequest = Communication.Game.SpinRequest;
var GameModules = Communication.Game.GameModules;
var CollectRequest = Communication.Game.CollectRequest;

function logDebug(msg: string): void {
    if (DEBUG) {
        Logger.debugGroup('ForceToolInterceptor', msg);
    }
}

function logError(msg: string): void {
    if (DEBUG) {
        Logger.errorGroup('ForceToolInterceptor', msg);
    }
}

const contextKeys = {
    ForceSet: 'ForceSet',
    ForceCommandIndex: 'ForceCommandIndex',
    ForceUIDisabled: 'ForceUIDisabled'
};

@Communication.Game.gameClientInterceptor()
export class ForceToolInterceptor implements IGameClientInterceptor {
    private _initialized: boolean;
    private _externalSetupInfo: ForceToolExternalSetupInfo;
    private _forceLayout: ForceToolLayout;

    constructor() {
        const self = this;
        this._externalSetupInfo = null;
        this._forceLayout = null;
        const externalSetup: ForceToolExternalSetup = {
            initialize: function (info: ForceToolExternalSetupInfo) {
                self._externalSetupInfo = info;
            }
        };
        window['ForceToolExternalSetup'] = externalSetup;
    }

    private get isEnabled(): boolean {
        const query = URI().query(true);
        let forceToolEnabled = Utils.asBool(Utils.valueOrDefault(query['force'], false));

        if (forceToolEnabled !== true) {
            const Symbol = window['Symbol'] as any;
            if (typeof Symbol === 'function') {
                // if Symbol is supported (newer browsers) check force flag here...
                const forFn = Symbol.for;
                if (typeof forFn === 'function') {
                    const symbolVar = forFn('NeoForceToolEnabledSymbol');
                    if (symbolVar) {
                        const enabledValue = window[symbolVar];
                        forceToolEnabled = Utils.asBool(Utils.valueOrDefault(enabledValue, false));
                    }
                }
            }
        }

        if (forceToolEnabled) {
            if (DEBUG) {
                logDebug('Force tool is enabled in query string.');
            }

            const isForceToolEnabledInApp = this._externalSetupInfo ? this._externalSetupInfo.isForceToolEnabled : null;

            if (Utils.isNullOrUndefined(isForceToolEnabledInApp)) {
                if (DEBUG) {
                    logDebug('Force tool will be disabled, app does not register callback to tell if forcetool can be enabled.');
                }

                forceToolEnabled = false;
            } else {
                forceToolEnabled = isForceToolEnabledInApp();

                if (DEBUG) {
                    const enabMsg = forceToolEnabled ? 'enabled' : 'disabled';
                    logDebug(`Force tool will be ${enabMsg}, app callback returns that!`);
                }
            }
        } else {
            if (DEBUG) {
                logDebug('Force tool is not enabled in query string!');
            }
        }

        return forceToolEnabled;
    }

    private get layout(): ForceToolLayout {
        if (this._forceLayout === null) {
            let forceLayout = ForceToolLayout.Demo;

            if (DEBUG) {
                const query = URI().query(true);
                const layout = Utils.valueOrDefault(query['force.layout'], ForceToolLayout.Demo);
                if (Utils.valueIsIn(layout, [ForceToolLayout.Debug, ForceToolLayout.Demo, ForceToolLayout.DemoDebug])) {
                    forceLayout = layout;
                }
            } else {
                if (!Utils.isNullOrUndefined(this._externalSetupInfo)) {
                    const demoDebugEnabled = this._externalSetupInfo.isForceToolDebugEnabled();
                    forceLayout = demoDebugEnabled ? ForceToolLayout.DemoDebug : ForceToolLayout.Demo;
                }
            }

            this._forceLayout = forceLayout;
        }

        return this._forceLayout;
    }

    private get initialized(): boolean {
        return this._initialized;
    }

    private initialize(): Promise<void> {
        const self = this;
        return Promise.resolve()
            .then(() => {
                if (!self.initialized) {
                    self._initialized = true;
                    return ForceToolUI.initialize(self.layout, self._externalSetupInfo);
                }
            });
    }

    private force(context: InterceptorRequestContext): Promise<any> {
        const self = this;
        return this.initialize()
            .then(ForceToolApi.getCurrentForceSet)
            .then(forceSet => {
                if (forceSet) {
                    const msgHeader = `ForceSet[${forceSet.Name}/${forceSet.File}] `;
                    const requestModule = context.module.toLowerCase();
                    const requestAction = context.request.actionName().toLowerCase();
                    const requestRoute = `${requestModule}/${requestAction}`;

                    const commandIndex = ForceToolApi.getCurrentCommandIndex();
                    if (forceSet.Commands && commandIndex > -1 && forceSet.Commands.length > commandIndex) {
                        const command = forceSet.Commands[commandIndex];
                        const commandIsValid = ForceToolApi.isValidForceCommand(command);

                        if (commandIsValid) {
                            const commandModule = command.Module;
                            const commandHasModule = !StringUtils.isNullOrEmpty(commandModule);
                            const commandAction = command.Action;
                            const commandHasAction = !StringUtils.isNullOrEmpty(commandAction);
                            const commandRoute = `${command.Module}/${command.Action}`.toLowerCase();

                            let routeMatch = true;
                            if (commandHasModule && commandHasAction) {
                                routeMatch = requestRoute === commandRoute;
                            } else if (!commandHasAction && commandHasModule) {
                                routeMatch = requestModule === commandModule;
                            } else if (!commandHasModule && commandHasAction) {
                                routeMatch = requestAction === commandAction;
                            }

                            if (routeMatch) {
                                if (StringUtils.isNullOrEmpty(command.Command)) {
                                    if (DEBUG) {
                                        logDebug(msgHeader + `Force command skipped, command at index (${commandIndex}) is empty.`);
                                    }
                                } else {
                                    if (DEBUG) {
                                        logDebug(msgHeader + `Sending force command [${command.Command}] to '${requestRoute}'`);
                                    }

                                    // Set force attribute on request to specified force set command
                                    (context.request as any).force = command.Command;

                                    // update interceptor request context to mark that for current request, force command was used.
                                    context.context[contextKeys.ForceUIDisabled] = true;

                                    ForceToolUI.updateState(ForceToolUIState.Disabled);
                                }

                                // update interceptor request context to mark that for current request, force command was used.
                                context.context[contextKeys.ForceCommandIndex] = commandIndex;
                                context.context[contextKeys.ForceSet] = forceSet;
                            } else {
                                if (DEBUG) {
                                    logDebug(msgHeader + `Force command skipped, current route '${requestRoute}' does not match command's '${commandRoute}'.`);
                                }
                            }
                        } else {
                            if (DEBUG) {
                                logError(msgHeader + `No force command was sent, command at index (${commandIndex}) points to invalid command data.`);
                            }
                        }
                    } else {
                        if (DEBUG) {
                            const cmdCount = Utils.isNullOrUndefined(forceSet.Commands) ? 0 : forceSet.Commands.length;
                            logError(msgHeader + `No force command was sent, command index (${commandIndex}) is out of range of force set (${cmdCount}) commands.`);
                        }
                    }
                } else {
                    if (DEBUG) {
                        logDebug(`No force command to send, no force set is selected yet!`);
                    }
                }
            });
    }

    public onSendGameRequest(context: InterceptorRequestContext, sendRequestFn: SendRequestFunctionType): Promise<any> {
        return this.isEnabled
            ? this.force(context)
            : Promise.resolve();
    }

    public onRequestError(context: InterceptorRequestContext, clientError: ApiClientError): void {
        const forceUIWasDisabled = Utils.valueOrDefault(context.context[contextKeys.ForceUIDisabled], false);
        if (forceUIWasDisabled) {
            // enable force UI on error!
            ForceToolUI.updateState(ForceToolUIState.Enabled);
            context.context[contextKeys.ForceUIDisabled] = false;
        }
    }

    public onResponseReceived<TResponse extends ResponseBase>(context: InterceptorRequestContext, response: TResponse): Promise<any> {
        return Promise.resolve();
    }

    public onSendGameRequestFinal(context: InterceptorRequestContext): void {
        const forceCommandIndex = Utils.valueOrDefault(context.context[contextKeys.ForceCommandIndex], -1);
        const forceSet = context.context[contextKeys.ForceSet] as ForceToolSet;
        if (forceCommandIndex > -1 && !Utils.isNullOrUndefined(forceSet)) {
            ForceToolUI.notifyCommandSent(forceSet, forceCommandIndex);
        }

        const forceUIWasDisabled = Utils.valueOrDefault(context.context[contextKeys.ForceUIDisabled], false);
        if (forceUIWasDisabled) {
            ForceToolUI.updateState(ForceToolUIState.Enabled);
            context.context[contextKeys.ForceUIDisabled] = false;
        }
    }
}


// WEBPACK FOOTER //
// ./src/ForceToolInterceptor.ts